import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

function extractInviteToken(redirect: string | null): string | null {
  if (!redirect) return null;
  try {
    const params = new URLSearchParams(redirect.split('?')[1] ?? '');
    return redirect.includes('accept-invite') ? params.get('token') : null;
  } catch { return null; }
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const inviteToken = extractInviteToken(redirect);

  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '', phone: '', companyName: '', companyDomain: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [companyHint, setCompanyHint] = useState<{ exists: boolean; name: string } | null>(null);
  const [inviteCompany, setInviteCompany] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteToken) return;
    api.get<{ valid: boolean; companyName?: string }>(`/companies/invite-info?token=${inviteToken}`)
      .then(r => { if (r.valid && r.companyName) setInviteCompany(r.companyName); })
      .catch(() => {});
  }, [inviteToken]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  async function checkCompanyName() {
    const name = form.companyName.trim();
    if (name.length < 2) { setCompanyHint(null); return; }
    try {
      const res = await api.get<{ exists: boolean; companyName: string | null }>(`/companies/check?name=${encodeURIComponent(name)}`);
      if (res.exists) setCompanyHint({ exists: true, name: res.companyName ?? name });
      else setCompanyHint(null);
    } catch { setCompanyHint(null); }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      if (!acceptedTerms) { setError('Please accept the Terms & Conditions and Privacy Policy.'); setLoading(false); return; }
      await api.post<{ pending: boolean; message: string }>('/auth/register', { ...form, redirect, acceptedTerms });
      navigate('/verify-pending', { state: { email: form.email, redirect } });
    } catch (err: any) {
      setError(err.message ?? 'Registration failed.');
    } finally { setLoading(false); }
  }

  const loginHref = `/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`;

  return (
    <div className="min-h-screen flex" style={{ background: '#f4f4f4' }}>

      {/* ── Left panel ── */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 px-16 py-12"
        style={{ background: '#f4f4f4', borderRight: '1px solid #e5e5e5' }}>
        <img src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
          alt="MONTIPOWER" style={{ height: 28, width: 'auto', objectFit: 'contain', objectPosition: 'left' }} />
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#e00000' }}>Warranty Portal</p>
          <h1 className="text-4xl font-bold leading-tight mb-6" style={{ color: '#0b0d0e', fontFamily: "'Archivo', sans-serif" }}>
            One portal for<br />all your tools.
          </h1>
          <p style={{ color: '#8f9b9b', fontSize: 15, lineHeight: 1.7 }}>
            Register your equipment, track warranty coverage, manage claims, and connect with your local Montipower distributor.
          </p>

          {/* FIVE benefits */}
          <div className="mt-8 rounded-xl overflow-hidden" style={{ border: '1px solid #e5e5e5' }}>
            <div style={{ height: 3, background: '#e00000' }} />
            <div className="px-5 py-4" style={{ background: '#ffffff' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#e00000' }}>
                Register to unlock FIVE
              </p>
              <p className="font-bold text-sm mb-3" style={{ color: '#0b0d0e' }}>
                3 Years. 5 Assurances. 1 Original System.
              </p>
              <ul className="space-y-1.5 mb-3">
                {[
                  '3 Years Protection',
                  'Original Belts. Engineered Performance.',
                  'The Original System Makes the Difference',
                  'The Right Technology. Applied the Right Way.',
                  'Every Original Can Be Proven.',
                ].map((title, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#0b0d0e' }}>
                    <span style={{ color: '#e00000', fontWeight: 700, flexShrink: 0 }}>→</span>
                    <span>{title}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs italic" style={{ color: '#8f9b9b' }}>
                Master the Surface. Master the Coating.
              </p>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 12, color: '#8f9b9b' }}>© {new Date().getFullYear()} MONTI Surface Tech Group B.V. · All rights reserved.</p>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="lg:hidden mb-8 text-center">
          <img src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
            alt="MONTIPOWER" style={{ height: 24, width: 'auto', margin: '0 auto 6px' }} />
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#aaa' }}>Warranty Portal</p>
        </div>

        <div className="w-full max-w-sm">

          {/* Invite context banner */}
          {inviteCompany && (
            <div className="mb-5 px-4 py-3 rounded-lg flex items-center gap-3" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: '#fff0f0' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e00000" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/>
                </svg>
              </div>
              <p className="text-sm" style={{ color: '#374151' }}>
                You've been invited to join <strong style={{ color: '#0b0d0e' }}>{inviteCompany}</strong>. Create your account to accept.
              </p>
            </div>
          )}

          <h2 className="text-2xl font-bold mb-1" style={{ color: '#0b0d0e' }}>{t('auth.registerTitle')}</h2>
          <p className="text-sm mb-6" style={{ color: '#8f9b9b' }}>{t('auth.registerSubtitle')}</p>

          {error && <div className="alert-error mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">{t('auth.firstName')}</label>
                <input className="input" value={form.firstName} onChange={set('firstName')} placeholder="Jan" /></div>
              <div><label className="label">{t('auth.lastName')}</label>
                <input className="input" value={form.lastName} onChange={set('lastName')} placeholder="de Vries" /></div>
            </div>

            <div>
              <label className="label">{t('auth.email')} <span style={{ color: '#e00000' }}>*</span></label>
              <input className="input" type="email" required value={form.email} onChange={set('email')} placeholder="you@example.com" autoComplete="email" />
            </div>

            {/* Hide company fields when coming from an invite */}
            {!inviteCompany && (
              <>
                <div>
                  <label className="label">Company name</label>
                  <input className="input" value={form.companyName}
                    onChange={e => { set('companyName')(e); setCompanyHint(null); }}
                    onBlur={checkCompanyName} placeholder="Acme Corp" />
                  {companyHint?.exists && (
                    <div className="mt-2 p-3 rounded-lg text-sm" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
                      <p className="font-semibold mb-0.5" style={{ color: '#9a3412' }}>
                        <strong>{companyHint.name}</strong> already has a portal account.
                      </p>
                      <p style={{ color: '#c2410c', fontSize: 13 }}>
                        Do your colleagues already use it? After registering, go to <strong>Settings → Company</strong> and enter your koppelcode to join.
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="label">Company domain <span style={{ color: '#8f9b9b', fontWeight: 400 }}>(optional)</span></label>
                  <input className="input" value={form.companyDomain} onChange={set('companyDomain')} placeholder="acmecorp.com" />
                </div>
              </>
            )}

            <div>
              <label className="label">Phone <span style={{ color: '#8f9b9b', fontWeight: 400 }}>(optional)</span></label>
              <input className="input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+31 6 …" />
            </div>

            <div>
              <label className="label">{t('auth.password')} <span style={{ color: '#e00000' }}>*</span></label>
              <input className="input" type="password" required minLength={8} value={form.password} onChange={set('password')}
                placeholder="Min. 8 characters" autoComplete="new-password" />
            </div>

            {/* Terms acceptance */}
            <div className="flex gap-3 items-start p-4 rounded-lg" style={{ background: acceptedTerms ? '#f0fdf4' : '#fafafa', border: `1px solid ${acceptedTerms ? '#86efac' : '#e5e5e5'}`, cursor: 'pointer' }}
              onClick={() => setAcceptedTerms(v => !v)}>
              <div className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center mt-0.5"
                style={{ background: acceptedTerms ? '#16a34a' : '#fff', border: `2px solid ${acceptedTerms ? '#16a34a' : '#d9d9d9'}` }}>
                {acceptedTerms && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
              </div>
              <p className="text-sm" style={{ color: '#374151', lineHeight: 1.6 }} onClick={e => e.stopPropagation()}>
                {t('auth.terms')}{' '}
                <a href="https://montipower.com/pages/terms-conditions" target="_blank" rel="noopener noreferrer"
                  style={{ color: '#e00000', fontWeight: 600 }} onClick={e => e.stopPropagation()}>{t('auth.termsLink')}</a>
                {' '}{t('auth.and')}{' '}
                <a href="https://montipower.com/pages/privacy-policy" target="_blank" rel="noopener noreferrer"
                  style={{ color: '#e00000', fontWeight: 600 }} onClick={e => e.stopPropagation()}>{t('auth.privacyLink')}</a>
                , and I understand that my data will be processed in accordance with these policies.
              </p>
            </div>

            <button className="btn-primary w-full" type="submit" disabled={loading || !acceptedTerms}
              style={{ opacity: !acceptedTerms ? 0.5 : 1 }}>
              {loading ? t('auth.creating') : inviteCompany ? `${t('auth.register')} & join ${inviteCompany}` : t('auth.register')}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{ color: '#8f9b9b' }}>
            {t('auth.haveAccount')}{' '}
            <Link to={loginHref} style={{ color: '#e00000', fontWeight: 600 }}>{t('auth.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
